import type {PropertyDefinition, PropertyValue} from '@github-ui/custom-properties-types'
import {getEmptyValueByType, isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {KebabHorizontalIcon, NoEntryFillIcon, SyncIcon, UndoIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, FormControl, IconButton, Text, TextInput, Token} from '@primer/react'

const mixedValuePlaceholder = '(Mixed)'

export function CustomPropertyEditControl({
  propertyValue,
  definition,
  changed,
  mixed = false,
  block = true,
  showUndo = true,
  error,
  onChange,
  onReset,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinition
  changed: boolean
  mixed?: boolean
  block?: boolean
  showUndo?: boolean
  error?: string
  onChange: (value: PropertyValue) => void
  onReset(): void
}) {
  const {propertyName, description} = definition
  const undoTitle = `Undo ${propertyName} changes`

  const editResetDropdownEnabled = useFeatureFlag('custom_properties_edit_reset_dropdown')

  return (
    <FormControl>
      <FormControl.Label data-testid="property-name">{getControlLabel(definition)}</FormControl.Label>
      <Box sx={{display: 'flex', width: '100%', gap: 2, alignItems: 'center'}}>
        <CustomPropertyInput
          propertyDefinition={definition}
          propertyValue={propertyValue}
          mixed={mixed}
          block={block}
          onChange={onChange}
        />
        {editResetDropdownEnabled ? (
          <ResetDropdown required={definition.required} {...{propertyName, changed, showUndo, onReset, onChange}} />
        ) : (
          <Box sx={{visibility: changed ? 'visible' : 'hidden'}}>
            <IconButton icon={() => <UndoIcon />} onClick={onReset} aria-label={undoTitle} variant="invisible" />
          </Box>
        )}
      </Box>
      {error && <FormControl.Validation variant="error">{error}</FormControl.Validation>}
      <FormControl.Caption>{description}</FormControl.Caption>
    </FormControl>
  )
}

interface CustomPropertyInputProps {
  propertyDefinition: PropertyDefinition
  propertyValue?: PropertyValue
  mixed: boolean
  block: boolean
  onChange: (value: PropertyValue) => void
}
export function CustomPropertyInput(props: CustomPropertyInputProps) {
  const {valueType} = props.propertyDefinition
  const value = props.propertyValue || getEmptyValueByType(valueType)

  switch (valueType) {
    case 'single_select':
      return <SingleSelectPropertyInput {...props} propertyValue={value as string} />
    case 'multi_select':
      return <MultiSelectPropertyInput {...props} propertyValue={value as string[]} />
    case 'boolean':
      return (
        <SingleSelectPropertyInput
          {...{...props, propertyDefinition: {...props.propertyDefinition, allowedValues: ['true', 'false']}}}
          propertyValue={value as string}
        />
      )

    case 'string':
      return <StringPropertyInput {...props} propertyValue={value as string} />
    default:
      return null
  }
}

interface SingleSelectInputProps extends CustomPropertyInputProps {
  propertyValue: string
}
function SingleSelectPropertyInput({
  propertyDefinition,
  propertyValue,
  mixed,
  block,
  onChange,
}: SingleSelectInputProps) {
  const {allowedValues, defaultValue, propertyName} = propertyDefinition
  const placeholder = defaultValue ? `${defaultValue} (default)` : 'Choose an option'
  const dropdownDisplayValue = propertyValue || placeholder

  return (
    <ActionMenu>
      <ActionMenu.Button
        aria-label={mixed ? mixedValuePlaceholder : `Select ${propertyName}`}
        block={block}
        sx={{span: {flex: 'none'}}}
      >
        {mixed ? mixedValuePlaceholder : dropdownDisplayValue}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium" sx={{maxHeight: '300px', overflowY: 'auto'}}>
        <ActionList selectionVariant="single">
          {(allowedValues || []).map(allowedValue => {
            const isSelected = allowedValue === propertyValue
            return (
              <ActionList.Item
                key={allowedValue}
                selected={isSelected}
                onSelect={() => onChange(isSelected ? '' : allowedValue)}
              >
                {allowedValue}
                {isSelected && (
                  <ActionList.TrailingVisual sx={{ml: 1}}>
                    <XIcon />
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
          {defaultValue && (
            <>
              <ActionList.Divider />
              <ActionList.Item onSelect={() => onChange('')}>
                <Box sx={{fontWeight: 'normal'}}>Default value ({defaultValue})</Box>
                <ActionList.Description variant="block" sx={{color: 'fg.muted'}}>
                  Inherit the default value set by the organization
                </ActionList.Description>
              </ActionList.Item>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

interface MultiSelectInputProps extends CustomPropertyInputProps {
  propertyValue: string[]
}
function MultiSelectPropertyInput({propertyDefinition, propertyValue, mixed, block, onChange}: MultiSelectInputProps) {
  const {allowedValues, defaultValue, propertyName} = propertyDefinition
  const placeholder = defaultValue ? `${defaultValue} (default value)` : 'Choose an option'
  const dropdownDisplayValue = propertyValue.join(', ') || placeholder

  return (
    <ActionMenu>
      <ActionMenu.Button
        aria-label={mixed ? mixedValuePlaceholder : `Select ${propertyName}`}
        block={block}
        sx={{span: {flex: 'none'}}}
      >
        {mixed ? mixedValuePlaceholder : dropdownDisplayValue}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium" sx={{maxHeight: '300px', overflowY: 'auto'}}>
        <ActionList selectionVariant="multiple">
          {(allowedValues || []).map(allowedValue => {
            const isSelected = propertyValue.includes(allowedValue)
            return (
              <ActionList.Item
                key={allowedValue}
                selected={isSelected}
                onSelect={() =>
                  isSelected
                    ? onChange(propertyValue.filter(v => v !== allowedValue))
                    : onChange(propertyValue.concat([allowedValue]))
                }
              >
                {allowedValue}
                {isSelected && (
                  <ActionList.TrailingVisual sx={{ml: 1}}>
                    <XIcon />
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

interface StringInputProps extends CustomPropertyInputProps {
  propertyValue: string
}
function StringPropertyInput({propertyDefinition, propertyValue, mixed, block, onChange}: StringInputProps) {
  const {defaultValue, propertyName} = propertyDefinition
  const placeholder = mixed ? mixedValuePlaceholder : defaultValue || ''
  return (
    <TextInput
      block={block}
      aria-label={propertyName}
      onChange={event => onChange(event.target.value)}
      value={propertyValue}
      placeholder={placeholder}
    />
  )
}

export function CustomPropertyReadOnlyControl({
  propertyValue,
  definition,
  showPermissionMessage = false,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinition
  showPermissionMessage?: boolean
}) {
  const {description} = definition

  return (
    <FormControl>
      <FormControl.Label data-testid="readonly-property-name">{getControlLabel(definition)}</FormControl.Label>
      <Box sx={{py: 1}}>
        <DisplayPropertyValue propertyValue={propertyValue} definition={definition} />
      </Box>
      <FormControl.Caption>
        <Box>{description}</Box>
        {showPermissionMessage && (
          <Box sx={{mt: 1, display: 'flex', gap: 1, fontWeight: 600, alignItems: 'center'}}>
            <NoEntryFillIcon size={12} />
            <Text>You don&apos;t have permission to edit this property</Text>
          </Box>
        )}
      </FormControl.Caption>
    </FormControl>
  )
}

export function DisplayPropertyValue({
  propertyValue,
  definition,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinition
}) {
  if (isEmptyPropertyValue(propertyValue)) {
    return <>(Empty)</>
  }

  switch (definition.valueType) {
    case 'single_select':
    case 'boolean':
      return <Token sx={{color: 'fg.default'}} text={propertyValue} />
    case 'multi_select':
      return (
        <>
          {(propertyValue as string[]).map(v => (
            <Token key={v} sx={{color: 'fg.default', mr: 1}} text={v} />
          ))}
        </>
      )
    case 'string':
      return <>{propertyValue}</>
  }
}

export function getControlLabel({propertyName, required}: PropertyDefinition) {
  return `${propertyName}${required ? ' *' : ''}`
}

export function ResetDropdown({
  propertyName,
  required,
  changed,
  showUndo = true,
  onReset,
  onChange,
}: {
  propertyName: string
  required: boolean
  changed: boolean
  showUndo?: boolean
  onReset(): void
  onChange: (value: string) => void
}) {
  const undoTitle = `Undo ${propertyName} changes`
  const resetToDefaultTitle = `Reset ${propertyName} to default value`

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label={`Open reset options for ${propertyName}`}
        />
      </ActionMenu.Anchor>

      <ActionMenu.Overlay width="small">
        <ActionList>
          {showUndo && (
            <ActionList.Item
              onSelect={onReset}
              aria-label={undoTitle}
              inactiveText={changed ? undefined : 'No change to undo'}
            >
              <ActionList.LeadingVisual>
                <UndoIcon />
              </ActionList.LeadingVisual>
              <Text sx={{fontWeight: 'bold', display: 'block'}}>Undo</Text>
              <Text sx={{fontSize: 'small', color: 'fg.subtle'}}>Reset to the last used value</Text>
            </ActionList.Item>
          )}
          {required && (
            <ActionList.Item onSelect={() => onChange('')} aria-label={resetToDefaultTitle}>
              <ActionList.LeadingVisual>
                <SyncIcon />
              </ActionList.LeadingVisual>
              <Text sx={{fontWeight: 'bold', display: 'block'}}>Reset to default</Text>
              <Text sx={{fontSize: 'small', color: 'fg.subtle'}}>
                Use the default value defined by the organization
              </Text>
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ CustomPropertyEditControl.displayName ||= 'CustomPropertyEditControl' } catch {}
try{ CustomPropertyInput.displayName ||= 'CustomPropertyInput' } catch {}
try{ SingleSelectPropertyInput.displayName ||= 'SingleSelectPropertyInput' } catch {}
try{ MultiSelectPropertyInput.displayName ||= 'MultiSelectPropertyInput' } catch {}
try{ StringPropertyInput.displayName ||= 'StringPropertyInput' } catch {}
try{ CustomPropertyReadOnlyControl.displayName ||= 'CustomPropertyReadOnlyControl' } catch {}
try{ DisplayPropertyValue.displayName ||= 'DisplayPropertyValue' } catch {}
try{ ResetDropdown.displayName ||= 'ResetDropdown' } catch {}